import {
  EnumerationOptionDto,
  instanceOfNumericDataSetFilterDto,
  instanceOfTextValueListDto,
} from '@/api/generator';
import { truthy } from '@/utils/helper.ts';
import {
  EnumerationOptionDtoWithDisplayName,
  EnumerationValueListDtoWithDisplayName,
  hasDisplayValues,
  instanceOfEnumerationOptionDtoWithDisplayName,
  instanceOfEnumerationValueListDtoWithDisplayName,
  ISetupParameterFilterDisplay,
  NumericDataSetFilterDtoWithDisplayName,
  TextValueListDtoWithDisplayValues,
} from '@/views/TrendsSelectionView/types.ts';
import MiniSearch, { SearchResult } from 'minisearch';

function extractNestedFields(document: any, fieldName: string) {
  // handle Enumeration options array
  if (fieldName === 'options.displayName') {
    // Extract all names and join them
    if (document.options && Array.isArray(document.options)) {
      return document.options
        .filter((option: any): option is EnumerationOptionDto =>
          instanceOfEnumerationOptionDtoWithDisplayName(option),
        )
        .map((option: EnumerationOptionDtoWithDisplayName) => option.displayName ?? option.name)
        .join(' ');
    }
    return '';
  }

  // Regular nested property handling
  return fieldName.split('.').reduce((doc, key) => doc?.[key], document);
}

function tokenizeIuid(text: string, fieldName?: string) {
  const lowerText = text.toLowerCase();
  let iuidTokens: string[] = [];
  if (fieldName === 'iuid') {
    const ptwPrefix = lowerText.startsWith('ptw-') ? lowerText.slice(0, 4) : undefined;

    const iuidCategory = ptwPrefix ? lowerText.slice(4, 6) : lowerText.slice(0, 2);
    const iuidNumberWithLeadingZeros = ptwPrefix ? lowerText.slice(6) : lowerText.slice(2);
    const iuidNumber = parseInt(iuidNumberWithLeadingZeros).toString();

    iuidTokens = [
      text,
      lowerText,
      ptwPrefix,
      ptwPrefix?.toUpperCase(),
      ptwPrefix && `${iuidCategory}${iuidNumberWithLeadingZeros}`,
      ptwPrefix && `${iuidCategory.toUpperCase()}${iuidNumberWithLeadingZeros}`,
      iuidCategory,
      iuidCategory.toUpperCase(),
      iuidNumberWithLeadingZeros,
      iuidNumber,
    ].filter(truthy);
  }
  return iuidTokens;
}
export function normalizeSearchText(input: string): string {
  return (
    input
      .normalize('NFKD')
      // remove diacritics, e.g. ä -> a
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
  );
}
export function tokenizeDefault(text: string) {
  const normalized = normalizeSearchText(text);
  return (
    normalized
      // split on non-alphanumeric (unicode-aware)
      .split(/[^\p{Letter}\p{Number}]+/u)
      .filter((t) => t.length > 0)
  ); // keep 1-letter tokens like "x" / "y"
}

export function instantiateParameterSearch() {
  return new MiniSearch({
    fields: [
      'iuid',
      'options.displayName',
      'availableValues',
      'displayName',
      'displayValues',
      'unitAbbreviation',
    ],
    storeFields: [
      'iuid',
      'options',
      'availableValues',
      'displayName',
      'displayValues',
      'unitAbbreviation',
    ],
    extractField: extractNestedFields,
    tokenize: (text: string, fieldName?: string) => {
      const tokens = tokenizeDefault(text);
      // Index suffixes so a query matches anywhere in a token, not only as a prefix.
      const firstSuffixIndex = 1;
      const minimumSuffixLength = 2;
      const suffixes: string[] = [];
      for (const token of tokens) {
        for (let start = firstSuffixIndex; start + minimumSuffixLength <= token.length; start++) {
          suffixes.push(token.slice(start));
        }
      }
      return [...tokens, ...suffixes, ...tokenizeIuid(text, fieldName)];
    },
    processTerm: (term) => normalizeSearchText(term),
    // Removed default prefix: true to allow per-query control with `shouldUsePrefix()`
  });
}

/**
 * Determines whether to use prefix search based on the search terms.
 * Uses a more sophisticated approach to avoid unwanted prefix matches while
 * preserving legitimate prefix search functionality.
 */
export function shouldUsePrefix(searchTerms: string[]): boolean {
  // Always use prefix for single-term queries
  if (searchTerms.length <= 1) {
    return true;
  }

  // For multi-term queries, check if we have potential conflicts
  // Disable prefix only if we have single characters that could conflict
  // with longer terms (like "c" potentially matching "cax" prefix)

  const singleCharTerms = searchTerms.filter((term) => term.length === 1);
  const multiCharTerms = searchTerms.filter((term) => term.length > 1);

  // If no single char terms, always use prefix search
  if (singleCharTerms.length === 0) {
    return true;
  }

  // Check if any single char could be a prefix of any multi-char term
  const hasConflict = singleCharTerms.some((singleChar) =>
    multiCharTerms.some((multiChar) => multiChar.startsWith(singleChar)),
  );

  // Only disable prefix search if there's a potential conflict
  return !hasConflict;
}

function filterOptionsBasedOnSearch(
  parameter: EnumerationValueListDtoWithDisplayName,
  searchTerm: string,
): EnumerationValueListDtoWithDisplayName {
  if (!searchTerm || !parameter.options || !Array.isArray(parameter.options)) {
    return parameter;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  // Split search terms to handle multi-word searches
  const searchTerms = lowerSearchTerm.split(/\s+/).filter((term) => term.length > 0);

  const hasOptionMatch = parameter.options.some((option) =>
    searchTerms.some((term) => option.displayName?.toLowerCase().includes(term)),
  );

  if (hasOptionMatch) {
    return {
      ...parameter,
      options: parameter.options.filter((option) =>
        searchTerms.some((term) => option.displayName?.toLowerCase().includes(term)),
      ),
    };
  }

  return parameter;
}

function filterNumericValuesBasedOnSearch(
  parameter: NumericDataSetFilterDtoWithDisplayName,
  searchTerm: string,
): NumericDataSetFilterDtoWithDisplayName {
  if (!searchTerm || !parameter.availableValues || !Array.isArray(parameter.availableValues)) {
    return parameter;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  // Split search terms to handle multi-word searches
  const searchTerms = lowerSearchTerm.split(/\s+/).filter((term) => term.length > 0);

  // Check if any display values match the search terms
  const hasValueMatch = parameter.availableValues.some((value, index) => {
    const displayValue =
      parameter.displayValues?.[index] ?? `${value.value} ${value.unitAbbreviation}`;
    return searchTerms.some((term) => displayValue.toLowerCase().includes(term));
  });

  if (hasValueMatch) {
    // Filter both availableValues and displayValues arrays in sync
    const matchingIndices: number[] = [];
    parameter.availableValues.forEach((value, index) => {
      const displayValue =
        parameter.displayValues?.[index] ?? `${value.value} ${value.unitAbbreviation}`;
      if (searchTerms.some((term) => displayValue.toLowerCase().includes(term))) {
        matchingIndices.push(index);
      }
    });

    return {
      ...parameter,
      availableValues: parameter.availableValues.filter((_, index) =>
        matchingIndices.includes(index),
      ),
      displayValues: parameter.displayValues?.filter((_, index) => matchingIndices.includes(index)),
    };
  }

  return parameter;
}

function filterTextValuesBasedOnSearch(
  parameter: TextValueListDtoWithDisplayValues,
  searchTerm: string,
): TextValueListDtoWithDisplayValues {
  if (!searchTerm || !parameter.availableValues || !Array.isArray(parameter.availableValues)) {
    return parameter;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  // Split search terms to handle multi-word searches
  const searchTerms = lowerSearchTerm.split(/\s+/).filter((term) => term.length > 0);

  // Check if any available values match the search terms
  const hasValueMatch = parameter.availableValues.some((value, index) => {
    const displayValue = parameter.displayValues?.[index] ?? value;
    return searchTerms.some((term) => displayValue.toLowerCase().includes(term));
  });

  if (hasValueMatch) {
    // Filter both availableValues and displayValues arrays in sync
    const matchingIndices: number[] = [];
    parameter.availableValues.forEach((value, index) => {
      const displayValue = parameter.displayValues?.[index] ?? value;
      if (searchTerms.some((term) => displayValue.toLowerCase().includes(term))) {
        matchingIndices.push(index);
      }
    });

    return {
      ...parameter,
      availableValues: parameter.availableValues.filter((_, index) =>
        matchingIndices.includes(index),
      ),
      displayValues: parameter.displayValues?.filter((_, index) => matchingIndices.includes(index)),
    };
  }

  return parameter;
}

export function filterParameterFilters(
  searchInput: string | undefined,
  parameterFilters: ISetupParameterFilterDisplay[],
  searchResults: SearchResult[],
) {
  if (!searchInput) {
    return parameterFilters;
  }
  if (searchResults.length === 0) {
    return [];
  }

  const matchedIds = searchResults.map((result) => result.id);

  const matchingFilters = parameterFilters.filter((filter) => matchedIds.includes(filter.id));

  // Apply appropriate filtering based on parameter type
  return matchingFilters.map((filter) => {
    if (instanceOfEnumerationValueListDtoWithDisplayName(filter) && searchInput) {
      return filterOptionsBasedOnSearch(filter, searchInput);
    }
    if (instanceOfTextValueListDto(filter) && hasDisplayValues(filter) && searchInput) {
      return filterTextValuesBasedOnSearch(filter, searchInput);
    }
    if (instanceOfNumericDataSetFilterDto(filter) && hasDisplayValues(filter) && searchInput) {
      return filterNumericValuesBasedOnSearch(filter, searchInput);
    }
    return filter;
  });
}
